import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Share2 } from "lucide-react";
import { SocialIcons } from "./SocialIcons";
import { useApp } from "../context/AppContext";
import { useTranslation, PHONES } from "../i18n";

export const Contact = () => {
  const { lang } = useApp();
  const t = useTranslation(lang);

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="overline">{t.contact.overline}</span>
          <h2 className="font-serif-q5 text-4xl md:text-5xl mt-3 text-white">{t.contact.title}</h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 space-y-7"
          >
            <div data-testid="contact-address" className="flex gap-4 items-start">
              <div className="w-11 h-11 border border-gold flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-gold" />
              </div>
              <div>
                <p className="overline">Dirección</p>
                <p className="text-white mt-1.5 leading-relaxed">{t.contact.address}</p>
              </div>
            </div>

            <div data-testid="contact-phone" className="flex gap-4 items-start">
              <div className="w-11 h-11 border border-gold flex items-center justify-center flex-shrink-0">
                <Phone size={18} className="text-gold" />
              </div>
              <div>
                <p className="overline">{t.contact.phones_title}</p>
                <div className="mt-1.5 space-y-1">
                  {PHONES.map((p) => (
                    <a
                      key={p.tel}
                      href={`tel:${p.tel}`}
                      data-testid={`contact-phone-${p.tel}`}
                      className="block text-white hover:text-gold transition-colors"
                    >
                      {p.display}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div data-testid="contact-hours" className="flex gap-4 items-start">
              <div className="w-11 h-11 border border-gold flex items-center justify-center flex-shrink-0">
                <Clock size={18} className="text-gold" />
              </div>
              <div>
                <p className="overline">{t.contact.hours_title}</p>
                <p className="text-white mt-1.5">{t.contact.hoursOpen}</p>
                <p className="text-[#A91D11] text-sm mt-0.5 uppercase tracking-[0.15em]">{t.contact.hoursClosed}</p>
              </div>
            </div>

            <div data-testid="contact-social" className="flex gap-4 items-start">
              <div className="w-11 h-11 border border-gold flex items-center justify-center flex-shrink-0">
                <Share2 size={18} className="text-gold" />
              </div>
              <div>
                <p className="overline">{t.contact.socials}</p>
                <div className="mt-3">
                  <SocialIcons />
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-3 border border-q5 overflow-hidden h-[420px]"
          >
            <iframe
              data-testid="contact-map"
              src="https://www.google.com/maps?q=Calle%20de%20la%20Huerta%2027%2C%2028411%20Moralzarzal%2C%20Madrid&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.6) contrast(1.1)" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              title="Restaurante Q5 Map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { lang } = useApp();
  const t = useTranslation(lang);
  return (
    <footer data-testid="footer" className="border-t border-q5 bg-[#0A0A0A] py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-serif-q5 text-2xl text-gold tracking-wider">Q5</p>
        <SocialIcons variant="inline" />
        <p className="text-xs text-neutral-500">© {new Date().getFullYear()} Restaurante Q5 · {t.footer.rights}</p>
      </div>
    </footer>
  );
};
